import { useEffect, useState } from "react"
import { useUser, useClerk } from "@clerk/clerk-react"
import { useParams, useNavigate } from "react-router-dom"
import { Card } from "@/components/ui/card"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { ErrorMessage } from "@/components/ui/error-message"
import api from "@/services/api"
import { getUserStats, checkIsAdmin, toggleNotifications, getNotificationsStatus } from '@/services/api'
import { SyncUsers } from "@/components/admin/SyncUsers";
import { formatLargeNumber } from "@/lib/utils/formatters"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { toast } from "@/hooks/useToast"
import { NotificationsCardSkeleton } from "./NotificationsCardSkeleton"

interface TokenBalance {
  remaining: number
  total: number
  resetIn: number
  tier: string
}

interface UserProfile {
  id: string
  username: string
  firstName?: string
  imageUrl?: string
  stats: {
    posts: number
    followers: number
    following: number
  }
}

export function Profile() {
  const { username } = useParams<{ username: string }>()
  const { user: currentUser } = useUser()
  const { signOut: clerkSignOut } = useClerk()
  const navigate = useNavigate()
  
  const isCurrentUser = !username || username === currentUser?.username

  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false)
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        console.log('Profile mount - params:', { username, currentUser: currentUser?.username })
        
        if (!username) {
          if (currentUser?.username) {
            console.log('Redirecting to current user profile:', currentUser.username)
            navigate(`/profile/${currentUser.username}`, { replace: true })
          }
          return
        }

        // At this point we have a username, either fetch current user or other user
        if (username === currentUser?.username) {
          console.log('Fetching current user profile')
          const statsResponse = await getUserStats()
          
          setUserProfile({
            id: currentUser.id || '',
            username: currentUser.username || '',
            firstName: currentUser.firstName ?? '',
            imageUrl: currentUser.imageUrl,
            stats: statsResponse.data.data || {
              posts: 0,
              followers: 0,
              following: 0
            }
          })
          
          const tokenResponse = await api.get('/api/v1/tokens/balance')
          setTokenBalance(tokenResponse.data.data)
        } else {
          console.log('Fetching other user profile:', username)
          const profileResponse = await api.get(`/api/v1/users/${username}/profile`)
          console.log('Profile response:', profileResponse.data)
          
          if (profileResponse.data.success && profileResponse.data.data) {
            setUserProfile({
              id: profileResponse.data.data.id,
              username: profileResponse.data.data.username,
              firstName: profileResponse.data.data.firstName,
              imageUrl: profileResponse.data.data.imageUrl,
              stats: profileResponse.data.data.stats
            })
          } else {
            setError('Profile not found')
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error)
        setError('Failed to load profile')
      } finally {
        setLoading(false)
      }
    }

    const checkAdmin = async () => {
      const adminStatus = await checkIsAdmin()
      setIsAdmin(adminStatus)
    }

    fetchData()
    checkAdmin()
  }, [username, currentUser, navigate])

  useEffect(() => {
    const fetchNotificationsStatus = async () => {
      if (isCurrentUser) {
        try {
          const response = await getNotificationsStatus();
          setNotificationsEnabled(response.data.notificationsEnabled);
        } catch (error) {
          console.error('Error fetching notifications status:', error);
        }
      }
    };

    fetchNotificationsStatus();
  }, [isCurrentUser]);

  const handleNotificationsToggle = async (enabled: boolean) => {
    try {
      setIsNotificationsLoading(true);
      await toggleNotifications(enabled);
      setNotificationsEnabled(enabled);
      
      toast({
        title: "Notifications Updated",
        description: `Notifications are now ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error) {
      console.error('Error toggling notifications:', error);
      toast({
        title: "Error",
        description: "Failed to update notification settings",
        variant: "destructive",
      });
    } finally {
      setIsNotificationsLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!userProfile) return <ErrorMessage message="Profile not found" />

  const formatTimeRemaining = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  return (
    <div className="container mx-auto p-4 mb-16 md:mb-4 md:ml-64">
      <div className="flex gap-2 items-center mb-4">
        <SyncUsers />
        {isAdmin && (
          <Button 
            variant="destructive" 
            onClick={() => clerkSignOut()}
          >
            Logout
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Profile Header - Full width on small, adjusted for medium and large */}
        <Card className="lg:col-span-4 lg:col-start-2 lg:row-start-1 p-4 order-2 lg:order-none sm:col-span-1">
          <h1 className="text-2xl font-bold">{userProfile.firstName || 'User'}</h1>
          <p className="text-muted-foreground">@{userProfile.username}</p>
        </Card>

        {/* Profile Picture - Adjusted positioning for different screens */}
        <Card className="lg:col-start-1 lg:row-start-1 p-4 order-1 lg:order-none sm:col-span-1">
          <div className="w-full max-w-[150px] mx-auto lg:max-w-none aspect-square rounded-full bg-accent overflow-hidden">
            {userProfile.imageUrl && (
              <img 
                src={userProfile.imageUrl} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </Card>

        {/* Stats Column - Responsive adjustments */}
        <Card className="lg:row-span-4 lg:row-start-2 p-4 order-3 lg:order-none sm:col-span-2 lg:col-span-1">
          <div className="space-y-4">
            {isCurrentUser && tokenBalance && (
              <div className="border-b pb-4">
                <h3 className="font-bold text-lg mb-2">Tokens</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Available</p>
                    <p className="text-xl font-bold">{formatLargeNumber(tokenBalance.remaining)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Daily Limit</p>
                    <p>{formatLargeNumber(tokenBalance.total)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Resets In</p>
                    <p>{formatTimeRemaining(tokenBalance.resetIn)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Current Tier</p>
                    <p className="capitalize">{tokenBalance.tier}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Real Stats */}
            <div>
              <h3 className="font-bold">Posts</h3>
              <p>{formatLargeNumber(userProfile.stats?.posts || 0)}</p>
            </div>
            <div>
              <h3 className="font-bold">Following</h3>
              <p>{formatLargeNumber(userProfile.stats?.following || 0)}</p>
            </div>
            <div>
              <h3 className="font-bold">Followers</h3>
              <p>{formatLargeNumber(userProfile.stats?.followers || 0)}</p>
            </div>
          </div>
        </Card>

        {/* Grid Items - Responsive layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:col-span-4 gap-4 order-4 lg:order-none">
          {isCurrentUser && (
            isNotificationsLoading ? (
              <NotificationsCardSkeleton />
            ) : (
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <h3 className="font-medium">Notifications</h3>
                    <p className="text-sm text-muted-foreground">
                      Receive email and push notifications
                    </p>
                  </div>
                  <Switch
                    checked={notificationsEnabled ?? false}
                    onCheckedChange={handleNotificationsToggle}
                    disabled={isNotificationsLoading || notificationsEnabled === null}
                    aria-label="Toggle notifications"
                  />
                </div>
              </Card>
            )
          )}
          <Card className="p-4">Favorite Cards</Card>
          <Card className="p-4">Achievements</Card>
          <Card className="p-4">Featured Deck</Card>
          
          <Card className="p-4">Top Cards</Card>
          <Card className="p-4">Recent Posts</Card>
          <Card className="p-4">Badges</Card>
          <Card className="p-4">More</Card>
        </div>
      
        {/* Timeline - Full width on all screens */}
        <Card className="lg:col-span-4 lg:col-start-2 p-4 order-5 lg:order-none sm:col-span-2">
          <h2 className="text-xl font-bold">Activity Timeline</h2>
        </Card>
      </div>
    </div>
  )
} 